<template>
    <page-shell v-if="booking && booking.client" title="Booking Details">
        <div class="booking-detail-wrap">
        <div class="booking-detail">
                <div class="info-wrapper">
                    <span><span class="info-tag">Booking Number: </span>{{ booking.code }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Door Code: </span>{{ booking.door_code }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Email: </span>{{ booking.client.email }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Phone number: </span>{{ booking.client.phone_number }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Location: </span>{{ booking.xcube.location.name }}</span>
                </div>

                <div class="info-wrapper">
                    <span><span class="info-tag">X-Cube Name: </span>{{ booking.xcube.name }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Team name: </span>{{ booking.team.name }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Player Count: </span>{{ booking.player_count }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Language: </span>{{ booking.language }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Rating: </span>{{ booking.rating }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Location Rating: </span>{{ booking.rating_location }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Experience: </span>{{ booking.experience.name }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Minutes Played: </span>{{ booking.minutes_played }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Score: </span>{{ booking.score }}</span>
                    <font-awesome-icon v-if="
                        booking.results &&
                        booking.results[booking.results.length - 1].success
                    " class="finish-flag-icon" icon="fa-solid fa-flag-checkered" />
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Game State: </span>
                        <status-mark v-bind:checked="booking.minutes_played > 5"></status-mark>
                    </span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Consent to Share: </span>
                        <span class="positive-response" v-if="booking.consent_to_share">Yes</span>
                        <span v-else>No</span>
                    </span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Local Date-Time: </span>
                        <Format :datetime="booking.date" />
                    </span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">X-Cube Time: </span>{{
                        booking.xcube.location
                            ? getTimeInTimeZone(
                                booking.date,
                                booking.xcube.location.time_zone
                            )
                            : ""
                    }}</span>
                </div>
                <div class="info-wrapper">
                    <span><span class="info-tag">Deleted Time: </span>{{
                        booking.xcube.location
                            ? getTimeInTimeZone(
                                booking.deleted_at,
                                booking.xcube.location.time_zone
                            )
                            : ""
                    }}</span>
                </div>
                <div class="info-wrapper" v-if="booking.photos && booking.photos.length > 0">
                    <span>
                        <span class="info-tag">Photo: </span>
                        <img :src="getPhotoUrl(booking.photos[0].url)" class="photo" />
                    </span>
                </div>
                <div v-if="hasSyncBookingSystem && isOperator" class="info-wrapper">
                    <span class="info-tag">Warning:</span>
                    <span class="warning">If a client wants a refund or a voucher, please ask them to file a
                        complaint via my.x-cube.nl</span>
                </div>
                <div class="info-wrapper">
                    <span class="info-tag">Refund:</span>
                    <input type="checkbox" :value="true" v-model="booking.refund_requested"
                        :disabled="hasSyncBookingSystem && isOperator" @change="requestRefund($event)" />
                </div>
                <div class="info-wrapper">
                    <span class="info-tag">Voucher:</span>
                    <input type="checkbox" :value="true" v-model="booking.voucher_requested"
                        :disabled="hasSyncBookingSystem && isOperator" @change="requestVoucher($event)" />
                </div>
                <div class="info-wrapper">
                    <span class="info-tag">Demo:</span>
                    <input type="checkbox" :value="true" :disabled="isOperator" v-model="booking.demo"
                        @change="toggleDemo($event)" />
                </div>
                <div class="button-container">
                    <el-button @click="back">
                        <font-awesome-icon icon="fa-solid fa-arrow-left" />
                    </el-button>
                    <el-dropdown @command="handleCommand">
                        <el-button type="primary">
                            Actions<i class="el-icon-arrow-down el-icon--right"></i>
                        </el-button>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item v-if="canRecoverBooking" command="recoverBooking">Recover</el-dropdown-item>
                            <el-dropdown-item v-if="canDeleteBooking" command="deleteBooking">Delete</el-dropdown-item>
                            <el-dropdown-item v-if="canMoveBooking" command="moveBooking" >Move</el-dropdown-item>
                            <el-dropdown-item v-if="canFinishBooking && !booking.finished" command="finishBooking">Finish</el-dropdown-item>
                            <el-dropdown-item v-if="canResendBookingMail" command="handleResendBookingMail">Resend Booking Mail</el-dropdown-item>
                            <el-dropdown-item v-if="canEditBooking" command="editBooking">Edit</el-dropdown-item>
                            <el-dropdown-item v-if="isManagement" command="openInStrapi">Open in
                                Strapi</el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </div>
        </div>
        </div>
    </page-shell>
</template>

<script setup>
import { Message } from "element-ui";
import PageShell from "../shared/PageShell.vue";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { fetchBookingData } from "../../api/graphql/bookings";
import { updateBooking, finishBooking, resendBookingMail } from "../../api/rest";
import {
    isManagement as determineIsManagement,
    isOperator as determineIsOperator,
} from "../../helpers/auth";
import { getTimeInTimeZone } from "../../helpers/dates";
import { toAbsoluteURL } from "../../helpers/download";
import useCurrentUserStore from "../../store/userStore";
import Format from "./../shared/Format.vue";
import StatusMark from "./../shared/StatusMark.vue";

const router = useRouter();
const route = useRoute();

const store = useCurrentUserStore();

const booking = ref(undefined);
const rating = ref(0);
const buttonText = ref(undefined);

const getPhotoUrl = (url) => {
    return toAbsoluteURL(url);
};

const handleCommand = (command) => {
    switch (command) {
        case "recoverBooking":
            recoverBooking();
            break;
        case "deleteBooking":
            deleteBooking();
            break;
        case "moveBooking":
            moveBooking();
            break;
        case "finishBooking":
            handleFinishBooking();
            break;
        case "handleResendBookingMail":
            handleResendBookingMail();
            break;
        case "editBooking":
            editBooking();
            break;
        case "openInStrapi":
            openInStrapi();
            break;
        default:
            console.error("Unknown command:", command);
            break;
    }
};


const hasSyncBookingSystem = computed(() => {
    return ['xpay', 'ireserve'].includes(booking.value.xcube.location.booking_system);
});

const recoverBooking = () => {
    router.push({
        name: "create-booking",
        params: { booking: booking.value },
    });
};

const moveBooking = () => {
    router.push({
        name: "move-booking",
        params: { id: booking.value.id },
    });
};

const openInStrapi = () => {
    const strapiUrl = import.meta.env.VITE_APP_API_URL;
    if (!strapiUrl) {
        Message.error({ message: "Strapi URL is not configured." });
        return;
    }
    const url = `${strapiUrl}/admin/plugins/content-manager/collectionType/application::booking.booking/${booking.value.id}`;
    console.log("Opening URL:", url);
    window.open(url, "_blank");
};

const editBooking = () => {
    router.push({
        name: "edit-booking",
        params: { id: booking.value.id },
    });
};

const deleteBooking = async () => {
    if (confirm("Are you sure you want to delete this booking?")) {
        const deleted_at = new Date().toISOString();
        const result = await updateBooking(booking.value.id, { deleted_at });
        booking.value.deleted_at = result.deleted_at;
    }
};

const back = () => {
    router.back();
};

const requestRefund = async () => {
    try {
        const result = await updateBooking(booking.value.id, {
            refund_requested: booking.value.refund_requested,
        });
        if (result.refund_requested) {
            Message.success({ message: "Refund requested" });
        } else {
            Message.success({ message: "Refund requested cancelled" });
        }
        booking.value.refund_requested = result.refund_requested;
    } catch (error) {
        Message.error({ message: `Error updating refund request: ${error.message}` });
        // Revert the change in the UI
        booking.value.refund_requested = !booking.value.refund_requested;
    }
};

const handleFinishBooking = async () => {
    await finishBooking(booking.value.id);
};

const requestVoucher = async () => {
    try {
        const result = await updateBooking(booking.value.id, {
            voucher_requested: booking.value.voucher_requested,
        });
        if (result.voucher_requested) {
            Message.success({ message: "Voucher requested" });
        } else {
            Message.success({ message: "Voucher requested cancelled" });
        }
        booking.value.voucher_requested = result.voucher_requested;
    } catch (error) {
        Message.error({ message: `Error updating voucher request: ${error.message}` });
        // Revert the change in the UI
        booking.value.voucher_requested = !booking.value.voucher_requested;
    }
};

const toggleDemo = async () => {
    try {
        const result = await updateBooking(booking.value.id, {
            demo: booking.value.demo,
        });
        if (result.demo) {
            Message.success({ message: "Demo enabled" });
        } else {
            Message.success({ message: "Demo disabled" });
        }
        booking.value.demo = result.demo;
    } catch (error) {
        Message.error({ message: `Error updating demo status: ${error.message}` });
        // Revert the change in the UI
        booking.value.demo = !booking.value.demo;
    }
};

const handleResendBookingMail = async () => {
    await resendBookingMail(booking.value.id, booking.value.client.email);
    Message.success({ message: `Resend booking mail to ${booking.value.client.email}` });
}

const isManagement = computed(() => determineIsManagement(store.currentUser));
const isOperator = computed(() => determineIsOperator(store.currentUser));

const canDeleteBooking = computed(() => {
    return (
        !booking.value.deleted_at &&
        (new Date().toISOString() < booking.value.date || isManagement.value)
    );
});

const canResendBookingMail = computed(() => {
    return (
        !booking.value.finished
    );
});


const canFinishBooking = computed(() => {
    return (
        !booking.value.finished &&
        booking.value.end_of_game_mail_sent &&
        isManagement.value
    );
});

const canMoveBooking = computed(() => {
    return !booking.value.minutes_played && (isManagement.value || isOperator.value);
});

const canRecoverBooking = computed(() => {
    return booking.value.deleted_at && (isManagement.value || isOperator.value);
});

const canEditBooking = computed(() => {
    return !booking.value.finished && (isManagement.value || isOperator.value);
});

onMounted(async () => {
    booking.value = route.params.id;

    if (typeof booking.value !== "object") {
        booking.value = await fetchBookingData(booking.value);
    }

    buttonText.value = !booking.value.deleted_at ? "Delete" : "Restore";
    rating.value =
        Number.isNaN(booking.value.rating) || booking.value.rating == null
            ? 0
            : booking.value.rating;
});
</script>

<style lang="scss" scoped>
@use "../../colors" as *;

.finish-flag-icon {
    padding-left: 0.5rem;
    color: $color-info;
}

.booking-detail-wrap {
    display: flex;
    justify-content: center;
}

.booking-detail {
    background-color: rgba(0, 0, 0, 0.7);
    border: 1px solid $color-primary;
    border-radius: 5px;
    min-width: 50%;
    width: max-content;
    max-width: 100%;
    padding: 50px;
}

img {
    width: 17px;
    height: 17px;
    z-index: 1;

    &.photo {
        width: 200px;
        height: auto;
        max-width: 100%;
    }
}

img:hover {
    opacity: 0.8;
}

.info-wrapper {
    text-align: left;
}

.bottom-info-wrapper {
    margin-bottom: 15px;
}

.info-wrapper>span {
    font-weight: lighter;
}

.info-tag {
    font-weight: bolder;
    color: $color-primary;
    display: inline-block;
    width: 400px;
    text-align: right;
    margin-right: 10px;
}

.positive-response {
    color: $color-success;
}

.button-container {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1rem;
}

@media (max-width: 1024px) {
    .info-tag {
        width: 120px;
        text-align: left;
        margin-right: 0;
    }
}
</style>
